import {
  DOC_TYPE_LABELS,
  type NumberSequence,
  NumberingList,
  formatDocNumber,
} from '@deizi/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Toggle } from '../../../components/Form';
import { Alert, ErrorBox, Skeleton } from '../../../components/States';
import { useToast } from '../../../components/Toast';
import { api } from '../../../lib/api';
import { errorMessage } from '../../../lib/messages';

const key = ['numbering'] as const;
const COLS = '1.1fr 90px 110px 110px 130px 1.4fr';
type Row = Pick<NumberSequence, 'docType' | 'prefix' | 'withYear' | 'resetYearly'> & {
  next: string;
};

/** Numeração tab: prefix, year, yearly reset and next number. */
export function NumberingTab({ readOnly }: { readOnly: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();
  const numbering = useQuery({
    queryKey: key,
    queryFn: () => api.get('/numbering', NumberingList),
  });
  const [rows, setRows] = useState<Row[] | null>(null);
  const [year] = useState(() => new Date().getFullYear());

  const base: Row[] = (numbering.data?.data ?? []).map((r) => ({
    ...r,
    next: String(r.nextValue),
  }));
  const shown = rows ?? base;
  const save = useMutation({
    mutationFn: () =>
      api.patch('/numbering', NumberingList, {
        items: shown.map((r) => ({ ...r, nextValue: Number.parseInt(r.next, 10) || 1 })),
      }),
    onSuccess: (data) => {
      qc.setQueryData(key, data);
      setRows(null);
      toast('Numeração salva. Números já usados não mudam.');
    },
  });

  const edit = (i: number, patch: Partial<Row>) =>
    setRows(
      shown.map((r, j) => {
        if (j !== i) return r;
        const next = { ...r, ...patch };
        if (!next.withYear) next.resetYearly = false;
        return next;
      }),
    );

  if (numbering.isPending) return <Skeleton rows={4} height={44} />;
  if (numbering.isError)
    return (
      <ErrorBox
        title="Não foi possível carregar a numeração"
        onRetry={() => void numbering.refetch()}
      />
    );

  return (
    <section className="card flat">
      <div className="row between wrap" style={{ padding: '16px 18px 6px' }}>
        <h2>Numeração dos documentos</h2>
        <span className="xs muted">Números já usados nunca mudam.</span>
      </div>
      <div className="gt" style={{ overflowX: 'auto' }}>
        <div className="gtr head stack-sm" style={{ gridTemplateColumns: COLS }}>
          <span>Documento</span>
          <span>Prefixo</span>
          <span>Com o ano</span>
          <span>Zerar todo ano</span>
          <span>Próximo nº</span>
          <span>Fica assim</span>
        </div>
        {shown.map((r, i) => {
          const label = DOC_TYPE_LABELS[r.docType];
          const value = Number.parseInt(r.next, 10) || 1;
          const prefix = r.prefix.toUpperCase() || '?';
          return (
            <div key={r.docType} className="gtr stack-sm" style={{ gridTemplateColumns: COLS }}>
              <span style={{ fontWeight: 500 }}>
                {label}
                <span className="xs muted" style={{ display: 'block' }}>
                  formato {r.withYear ? `${prefix}-AAAA-0000` : `${prefix}-0000`}
                </span>
              </span>
              <input
                className="cell"
                aria-label={`Prefixo de ${label}`}
                value={r.prefix}
                maxLength={4}
                onChange={(e) => edit(i, { prefix: e.target.value.toUpperCase() })}
                disabled={readOnly}
              />
              <Toggle
                on={r.withYear}
                onChange={(v) => edit(i, { withYear: v })}
                label={`Incluir o ano em ${label}`}
                disabled={readOnly}
              />
              <Toggle
                on={r.resetYearly}
                onChange={(v) => edit(i, { resetYearly: v })}
                label={`Zerar todo ano em ${label}`}
                disabled={readOnly || !r.withYear}
              />
              <input
                className="cell"
                aria-label={`Próximo número de ${label}`}
                inputMode="numeric"
                value={r.next}
                onChange={(e) => edit(i, { next: e.target.value.replace(/\D/g, '') })}
                disabled={readOnly}
              />
              <span>
                <b className="tab" style={{ fontWeight: 600, color: 'var(--pe)' }}>
                  {formatDocNumber(prefix, r.withYear ? year : null, value)}
                </b>
                <span className="xs muted" style={{ display: 'block' }}>
                  {r.resetYearly
                    ? `Em 01/01/${year + 1} recomeça em ${formatDocNumber(prefix, year + 1, 1)}`
                    : 'segue contando sem zerar'}
                </span>
              </span>
            </div>
          );
        })}
      </div>
      {save.isError ? (
        <div style={{ padding: '0 18px' }}>
          <Alert tone="err">{errorMessage(save.error)}</Alert>
        </div>
      ) : null}
      {!readOnly ? (
        <div
          className="row between wrap"
          style={{ padding: '14px 18px', gap: 10, borderTop: '1px solid var(--line)' }}
        >
          <span className="xs muted">
            “Zerar todo ano” só funciona com o ano no número, senão haveria dois Q-0001.
          </span>
          <button
            type="button"
            className="btn p"
            disabled={rows === null || save.isPending}
            onClick={() => save.mutate()}
          >
            Salvar numeração
          </button>
        </div>
      ) : null}
    </section>
  );
}
